import { enableMapSet } from 'immer'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

// The stream store keeps connection presence in `Set`s (liveAgentIds and the
// per-workspace liveUsers sets). Immer's default setup cannot proxy Set/Map,
// so enable the MapSet plugin once at module load. This configures the same
// default immer instance that the zustand immer middleware uses, so every
// produce() below can read and reassign Sets safely.
enableMapSet()

/**
 * Separator inside thread-scoped stream keys: `${topicId}::thr::${threadId}`.
 * Main-channel turns (no thread) use the bare topicId. Kept as a constant so
 * presence lookups can enumerate a topic's thread turns by prefix without
 * duplicating the format string.
 */
const THREAD_KEY_SEP = '::thr::'

/**
 * Stream-store key for a turn. Thread turns get their own namespace so the
 * thread panel and the main channel don't cross-render live deltas. Main-channel
 * turns (no thread) use the bare topicId.
 */
export function streamKey(topicId: string, threadId?: string | null): string {
  return threadId ? `${topicId}${THREAD_KEY_SEP}${threadId}` : topicId
}

/** Inverse of `streamKey`: split a turn key into its topic/thread parts. */
export function parseStreamKey(key: string): {
  topicId: string
  threadId: string | null
} {
  const i = key.indexOf(THREAD_KEY_SEP)
  return i === -1
    ? { topicId: key, threadId: null }
    : {
        topicId: key.slice(0, i),
        threadId: key.slice(i + THREAD_KEY_SEP.length),
      }
}

/**
 * Agent ids with a turn currently in flight anywhere in `topicId` — the main
 * channel (bare key) plus every thread-scoped turn. Every topic is thread-first:
 * the backend auto-threads each top-level message, so the responding turn runs
 * under a thread-scoped key (a new thread the user hasn't opened yet), and the
 * bare-key presence check alone would miss it and show no "typing" indicator.
 */
export function activeAgentsInTopic(
  state: StreamState,
  topicId: string,
): string[] {
  const agents = [...(state.activeTurns[topicId] ?? [])]
  const prefix = `${topicId}${THREAD_KEY_SEP}`
  for (const key in state.activeTurns) {
    if (key.startsWith(prefix)) {
      const turn = state.activeTurns[key]
      if (turn?.length) agents.push(...turn)
    }
  }
  return agents
}

interface StreamChunk {
  agent_id: string
  text: string
}

interface ToolActivity {
  agent_id: string
  tool_name: string
  active: boolean
}

interface SubAgentActivity {
  agent_id: string
  task_description: string
  persistent_name?: string
  parent_agent_id?: string
}

// A router decision for a topic — either a successful pick or a fallback
// (router configured but failed, so the primary/first agent was used instead).
interface RouteInfo {
  fallback: boolean
  agentId?: string // set when fallback === false
  model?: string // set when fallback === false
  candidates: string[]
  reason?: string // set when fallback === true
  ts: number
}

export interface ActivityEntry {
  id: string
  type:
    | 'tool_start'
    | 'tool_finish'
    | 'tool_progress'
    | 'sub_agent_spawn'
    | 'sub_agent_complete'
    | 'sub_agent_cancel'
    | 'message_complete'
    | 'agent_error'
    | 'content_chunk'
    | 'thinking_chunk'
    | 'resource_card'
  agentId: string
  ts: number
  data: Record<string, unknown>
}

interface StreamState {
  deltas: Record<string, StreamChunk[]>
  // Live chain-of-thought / reasoning text, keyed like `deltas` (streamKey).
  // Ephemeral: cleared on turn end, never persisted to history.
  thinking: Record<string, StreamChunk[]>
  tools: Record<string, ToolActivity[]>
  subAgents: Record<string, SubAgentActivity[]>
  knownPersistentSubAgents: Record<string, SubAgentActivity[]>
  // activityLog[topicId][agentId] = ActivityEntry[] — persists across turns
  activityLog: Record<string, Record<string, ActivityEntry[]>>
  // ephemeralAgentParents[topicId][subAgentId] = parentAgentId
  ephemeralAgentParents: Record<string, Record<string, string>>
  // subAgentDeltas[topicId][subAgentId] = accumulated live text for in-progress sub-agents
  subAgentDeltas: Record<string, Record<string, string>>
  // toolProgress[topicId][toolName] = latestProgressLine
  toolProgress: Record<string, Record<string, string>>
  // lastErrorAt[topicId] = ts of the last agent_error, so views can clear a
  // "waiting" indicator when a targeted agent fails with no reply coming.
  lastErrorAt: Record<string, number>
  // lastRoute[topicId] = the most recent router decision (route.selected /
  // route.fallback), so anyone opening the drawer later still sees it, not
  // just whoever was watching when it fired.
  lastRoute: Record<string, RouteInfo>
  // activeTurns[streamKey(topicId, threadId)] = agent ids with a turn in
  // flight. Authoritative presence, driven by turn_started / turn_ended (and
  // cleared defensively on message_complete / agent_error). Seeded from the WS
  // init payload so reconnects/new tabs show the indicator immediately.
  activeTurns: Record<string, string[]>
  // Agent ids currently live on the hub (seeded from the WS `init` `agents`
  // array). Drives the steady-green agent dot in the sidebar: an agent in the
  // live set but not running is "idle-but-live"; one outside the set is
  // offline/unregistered (grey).
  liveAgentIds: Set<string>
  // liveUsers[workspaceId] = set of user ids with at least one live hub
  // connection in that workspace. Seeded from the WS `init` `online_users`
  // snapshot and updated live by `user_presence` events.
  liveUsers: Record<string, Set<string>>
  setLiveAgents: (ids: string[]) => void
  setAgentOnline: (agentId: string, online: boolean) => void
  setLiveUsers: (workspaceId: string, ids: string[]) => void
  setUserOnline: (workspaceId: string, userId: string, online: boolean) => void
  clearLiveUsers: () => void
  // truncatedKeys[streamKey(topicId, threadId)] = the last completed turn on
  // that key ended with finish_reason Length (output truncated). Set from
  // message_complete; cleared when a new turn starts on the key (setTurnActive
  // true). Drives a "Continue" affordance so a cut-off reply is never silent.
  truncatedKeys: Record<string, boolean>
  markError: (topicId: string, ts: number) => void
  setLastRoute: (topicId: string, info: RouteInfo) => void
  markTruncated: (topicId: string, threadId: string | null | undefined) => void
  setTurnActive: (
    topicId: string,
    threadId: string | null | undefined,
    agentId: string,
    active: boolean,
  ) => void
  appendDelta: (topicId: string, agentId: string, text: string) => void
  appendThinking: (topicId: string, agentId: string, text: string) => void
  appendSubAgentDelta: (
    topicId: string,
    subAgentId: string,
    text: string,
  ) => void
  clearSubAgentDelta: (topicId: string, subAgentId: string) => void
  setToolActive: (
    topicId: string,
    agentId: string,
    toolName: string,
    active: boolean,
  ) => void
  setSubAgentActive: (
    topicId: string,
    agentId: string,
    description: string,
    active: boolean,
    persistentName?: string,
    parentAgentId?: string,
  ) => void
  addKnownPersistentSubAgent: (
    topicId: string,
    activity: SubAgentActivity,
  ) => void
  appendActivity: (
    topicId: string,
    agentId: string,
    entry: Omit<ActivityEntry, 'id'>,
  ) => void
  recordEphemeralAgent: (
    topicId: string,
    agentId: string,
    parentAgentId: string,
  ) => void
  setToolProgress: (topicId: string, toolName: string, line: string) => void
  clearToolProgress: (topicId: string, toolName: string) => void
  clearTopic: (topicId: string) => void
  clearAll: () => void
  clearAllStreaming: () => void
}

export const useStreamStore = create<StreamState>()(
  immer((set) => ({
    deltas: {},
    thinking: {},
    tools: {},
    subAgents: {},
    knownPersistentSubAgents: {},
    activityLog: {},
    ephemeralAgentParents: {},
    subAgentDeltas: {},
    toolProgress: {},
    lastErrorAt: {},
    lastRoute: {},
    activeTurns: {},
    liveAgentIds: new Set(),
    liveUsers: {},
    truncatedKeys: {},

    markError: (topicId, ts) =>
      set((s) => {
        s.lastErrorAt[topicId] = ts
      }),

    setLastRoute: (topicId, info) =>
      set((s) => {
        s.lastRoute[topicId] = info
      }),

    markTruncated: (topicId, threadId) =>
      set((s) => {
        s.truncatedKeys[streamKey(topicId, threadId)] = true
      }),

    setTurnActive: (topicId, threadId, agentId, active) =>
      set((s) => {
        const key = streamKey(topicId, threadId)
        const prev = s.activeTurns[key] ?? []
        if (active) {
          if (!prev.includes(agentId)) {
            s.activeTurns[key] = [...prev, agentId]
          }
          // A new turn starting supersedes any "last turn was truncated" state.
          if (s.truncatedKeys[key]) {
            delete s.truncatedKeys[key]
          }
        } else {
          const next = prev.filter((id) => id !== agentId)
          if (next.length > 0) {
            s.activeTurns[key] = next
          } else {
            delete s.activeTurns[key]
          }
        }
      }),

    // Presence state is owned by the WS layer, not derived from turns, so it
    // survives stream resets (clearAll/clearAllStreaming) — a reconnect must
    // re-seed it from the fresh init payload instead.

    setLiveAgents: (ids) =>
      set((s) => {
        s.liveAgentIds = new Set(ids)
      }),

    setAgentOnline: (agentId, online) =>
      set((s) => {
        const next = new Set(s.liveAgentIds)
        if (online) {
          next.add(agentId)
        } else {
          next.delete(agentId)
        }
        s.liveAgentIds = next
      }),

    setLiveUsers: (workspaceId, ids) =>
      set((s) => {
        s.liveUsers[workspaceId] = new Set(ids)
      }),

    setUserOnline: (workspaceId, userId, online) =>
      set((s) => {
        // immer without enableMapSet() cannot mutate a Set in place, so derive
        // a fresh Set from the existing members and reassign it.
        const next = new Set(s.liveUsers[workspaceId] ?? [])
        if (online) {
          next.add(userId)
        } else {
          next.delete(userId)
        }
        s.liveUsers[workspaceId] = next
      }),

    clearLiveUsers: () =>
      set((s) => {
        s.liveUsers = {}
      }),

    appendDelta: (topicId, agentId, text) =>
      set((s) => {
        const prev = s.deltas[topicId] ?? []
        const last = prev[prev.length - 1]
        if (last && last.agent_id === agentId) {
          last.text += text
        } else {
          prev.push({ agent_id: agentId, text })
          s.deltas[topicId] = prev
        }
      }),

    appendThinking: (topicId, agentId, text) =>
      set((s) => {
        const prev = s.thinking[topicId] ?? []
        const last = prev[prev.length - 1]
        if (last && last.agent_id === agentId) {
          last.text += text
        } else {
          prev.push({ agent_id: agentId, text })
          s.thinking[topicId] = prev
        }
      }),

    appendSubAgentDelta: (topicId, subAgentId, text) =>
      set((s) => {
        if (!s.subAgentDeltas[topicId]) s.subAgentDeltas[topicId] = {}
        const prev = s.subAgentDeltas[topicId][subAgentId] ?? ''
        s.subAgentDeltas[topicId][subAgentId] = prev + text
      }),

    clearSubAgentDelta: (topicId, subAgentId) =>
      set((s) => {
        delete s.subAgentDeltas[topicId]?.[subAgentId]
      }),

    setToolActive: (topicId, agentId, toolName, active) =>
      set((s) => {
        const prev = (s.tools[topicId] ?? []).filter(
          (t) => !(t.agent_id === agentId && t.tool_name === toolName),
        )
        if (active) {
          prev.push({ agent_id: agentId, tool_name: toolName, active })
        }
        s.tools[topicId] = prev
      }),

    setSubAgentActive: (
      topicId,
      agentId,
      description,
      active,
      persistentName,
      parentAgentId,
    ) =>
      set((s) => {
        const prev = (s.subAgents[topicId] ?? []).filter(
          (a) => a.agent_id !== agentId,
        )
        if (active) {
          prev.push({
            agent_id: agentId,
            task_description: description,
            persistent_name: persistentName,
            parent_agent_id: parentAgentId,
          })
        }
        s.subAgents[topicId] = prev
      }),

    addKnownPersistentSubAgent: (topicId, activity) =>
      set((s) => {
        const prev = s.knownPersistentSubAgents[topicId] ?? []
        if (prev.some((a) => a.agent_id === activity.agent_id)) return
        prev.push(activity)
        s.knownPersistentSubAgents[topicId] = prev
      }),

    appendActivity: (topicId, agentId, entry) =>
      set((s) => {
        if (!s.activityLog[topicId]) s.activityLog[topicId] = {}
        if (!s.activityLog[topicId][agentId])
          s.activityLog[topicId][agentId] = []
        const agentLog = s.activityLog[topicId][agentId]

        // Merge consecutive streaming text (content or thinking) from the same
        // source sub-agent into one entry to keep the feed compact.
        if (entry.type === 'content_chunk' || entry.type === 'thinking_chunk') {
          const last = agentLog[agentLog.length - 1]
          if (
            last?.type === entry.type &&
            last.data.source_agent_id === entry.data.source_agent_id
          ) {
            last.data.text =
              String(last.data.text ?? '') + String(entry.data.text ?? '')
            return
          }
        }

        const id = `${entry.ts}-${entry.type}-${agentId}-${agentLog.length}`
        agentLog.push({ ...entry, id })

        // Turn boundary: everything before it for the same thread is dead
        // weight — consumers only ever read the post-boundary tail
        // (sliceCurrentTurn) — so drop it. Keeps per-chunk work and memory
        // O(current turn) instead of growing all session. Guard: sub-agent
        // completions are logged against the parent while the parent's own
        // turn is still in flight, so only trim when the boundary belongs to
        // the log's owner.
        if (entry.type === 'message_complete' || entry.type === 'agent_error') {
          const actor = entry.data.agent_id
            ? String(entry.data.agent_id)
            : agentId
          if (actor === agentId) {
            const tid = entry.data.thread_id
              ? String(entry.data.thread_id)
              : null
            const boundary = agentLog.length - 1
            s.activityLog[topicId][agentId] = agentLog.filter(
              (e, i) =>
                i >= boundary ||
                (e.data.thread_id ? String(e.data.thread_id) : null) !== tid,
            )
          }
        }
      }),

    recordEphemeralAgent: (topicId, agentId, parentAgentId) =>
      set((s) => {
        if (!s.ephemeralAgentParents[topicId])
          s.ephemeralAgentParents[topicId] = {}
        s.ephemeralAgentParents[topicId][agentId] = parentAgentId
      }),

    setToolProgress: (topicId, toolName, line) =>
      set((s) => {
        if (!s.toolProgress[topicId]) s.toolProgress[topicId] = {}
        s.toolProgress[topicId][toolName] = line
      }),

    clearToolProgress: (topicId, toolName) =>
      set((s) => {
        delete s.toolProgress[topicId]?.[toolName]
      }),

    clearTopic: (topicId) =>
      set((s) => {
        s.deltas[topicId] = []
        s.thinking[topicId] = []
        s.tools[topicId] = []
        s.subAgents[topicId] = []
        s.subAgentDeltas[topicId] = {}
        s.toolProgress[topicId] = {}
      }),

    clearAll: () =>
      set((s) => {
        s.deltas = {}
        s.thinking = {}
        s.tools = {}
        s.subAgents = {}
        s.knownPersistentSubAgents = {}
        s.activityLog = {}
        s.ephemeralAgentParents = {}
        s.subAgentDeltas = {}
        s.toolProgress = {}
        s.lastErrorAt = {}
        s.lastRoute = {}
        s.activeTurns = {}
        s.truncatedKeys = {}
      }),

    clearAllStreaming: () =>
      set((s) => {
        s.deltas = {}
        s.thinking = {}
        s.tools = {}
        s.subAgents = {}
        s.subAgentDeltas = {}
        s.toolProgress = {}
        s.activeTurns = {}
        s.truncatedKeys = {}
        // Preserve: activityLog, knownPersistentSubAgents, ephemeralAgentParents
      }),
  })),
)
